import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useBuilderStore } from '../../../stores/builderStore';
import { fieldRegistry } from '../../../lib/fieldRegistry';
import type { FormField, FieldOption } from '../../../types/form';

interface MobileFieldConfiguratorProps {
  isOpen: boolean;
  onClose: () => void;
}

type Tab = 'general' | 'options' | 'validation' | 'advanced';

function TabButton({
  label,
  icon,
  isActive,
  onClick,
}: {
  label: string;
  icon: React.ReactNode;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center gap-1 px-4 py-2 rounded-xl transition-colors touch-manipulation ${
        isActive
          ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
          : 'text-gray-500 dark:text-gray-400'
      }`}
    >
      {icon}
      <span className="text-xs font-medium">{label}</span>
    </button>
  );
}

function OptionItem({
  option,
  index,
  onUpdate,
  onRemove,
  canRemove,
}: {
  option: FieldOption;
  index: number;
  onUpdate: (updates: Partial<FieldOption>) => void;
  onRemove: () => void;
  canRemove: boolean;
}) {
  // Use local state to prevent keyboard dismissal on mobile
  const [localLabel, setLocalLabel] = useState(option.label);

  // Sync local state when option changes externally
  useEffect(() => {
    setLocalLabel(option.label);
  }, [option.label]);

  // Commit changes to store when user finishes editing
  const handleBlur = () => {
    if (localLabel !== option.label) {
      onUpdate({
        label: localLabel,
        value: localLabel.toLowerCase().replace(/\s+/g, '_')
      });
    }
  };

  return (
    <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-xl">
      <span className="w-6 h-6 flex items-center justify-center bg-gray-200 dark:bg-gray-600 rounded-full text-xs font-medium text-gray-600 dark:text-gray-300">
        {index + 1}
      </span>
      <input
        type="text"
        value={localLabel}
        onChange={(e) => setLocalLabel(e.target.value)}
        onBlur={handleBlur}
        placeholder="Option label"
        className="flex-1 px-3 py-2 bg-white dark:bg-gray-600 border border-gray-200 dark:border-gray-500 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:text-white"
      />
      {canRemove && (
        <button
          onClick={onRemove}
          aria-label={`Remove option ${index + 1}`}
          className="p-2 text-gray-400 hover:text-red-500 rounded-lg touch-manipulation"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      )}
    </div>
  );
}

export default function MobileFieldConfigurator({ isOpen, onClose }: MobileFieldConfiguratorProps) {
  const { t } = useTranslation('mobile');
  const { formSchema, selectedFieldId, updateField, removeField, duplicateField } = useBuilderStore();
  const [activeTab, setActiveTab] = useState<Tab>('general');
  const [sheetHeight, setSheetHeight] = useState(70); // percentage
  const startY = useRef<number>(0);
  const currentHeight = useRef<number>(70);
  const sheetRef = useRef<HTMLDivElement>(null);

  const field = formSchema.fields.find((f) => f.id === selectedFieldId);
  const fieldDef = field ? fieldRegistry[field.type] : null;

  // Reset tab when field changes
  useEffect(() => {
    setActiveTab('general');
  }, [selectedFieldId]);

  // Handle drag to resize/close
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    startY.current = e.touches[0].clientY;
    currentHeight.current = sheetHeight;
  }, [sheetHeight]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const deltaY = startY.current - e.touches[0].clientY;
    const windowHeight = window.innerHeight;
    const deltaPercent = (deltaY / windowHeight) * 100;
    const newHeight = Math.max(30, Math.min(95, currentHeight.current + deltaPercent));
    setSheetHeight(newHeight);
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (sheetHeight < 40) {
      onClose();
      setSheetHeight(70);
    } else if (sheetHeight > 85) {
      setSheetHeight(95);
    } else {
      setSheetHeight(70);
    }
  }, [sheetHeight, onClose]);

  const handleChange = useCallback(<K extends keyof FormField>(key: K, value: FormField[K]) => {
    if (field) {
      updateField(field.id, { [key]: value });
    }
  }, [field, updateField]);

  const handleOptionsChange = useCallback((options: FieldOption[]) => {
    if (field) {
      updateField(field.id, { options });
    }
  }, [field, updateField]);

  const addOption = useCallback(() => {
    if (!field) return;
    const newOption: FieldOption = {
      label: `Option ${(field.options?.length || 0) + 1}`,
      value: `option${(field.options?.length || 0) + 1}`,
    };
    handleOptionsChange([...(field.options || []), newOption]);
  }, [field, handleOptionsChange]);

  const updateOption = useCallback((index: number, updates: Partial<FieldOption>) => {
    if (!field) return;
    const newOptions = [...(field.options || [])];
    newOptions[index] = { ...newOptions[index], ...updates };
    handleOptionsChange(newOptions);
  }, [field, handleOptionsChange]);

  const removeOption = useCallback((index: number) => {
    if (!field) return;
    const newOptions = (field.options || []).filter((_, i) => i !== index);
    handleOptionsChange(newOptions);
  }, [field, handleOptionsChange]);

  const handleDelete = useCallback(() => {
    if (field) {
      removeField(field.id);
      onClose();
    }
  }, [field, removeField, onClose]);

  const handleDuplicate = useCallback(() => {
    if (field) {
      duplicateField(field.id);
    }
  }, [field, duplicateField]);

  if (!isOpen || !field || !fieldDef) return null;

  const hasOptions = field.type === 'select' || field.type === 'radio' || field.type === 'checkbox';
  const hasValidation = field.type === 'text' || field.type === 'number' || field.type === 'email' || field.type === 'textarea';

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 animate-fadeIn"
        onClick={onClose}
      />

      {/* Bottom Sheet */}
      <div
        ref={sheetRef}
        className="absolute bottom-0 left-0 right-0 bg-white dark:bg-gray-800 rounded-t-3xl shadow-2xl animate-slideUp"
        style={{ height: `${sheetHeight}%` }}
      >
        {/* Drag Handle */}
        <div
          className="flex justify-center py-3 cursor-grab active:cursor-grabbing touch-manipulation"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="w-12 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full" />
        </div>

        {/* Header */}
        <div className="px-4 pb-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 flex items-center justify-center bg-primary-100 dark:bg-primary-900/50 text-primary-600 dark:text-primary-400 rounded-xl text-lg">
                {fieldDef.icon}
              </span>
              <div>
                <h2 className="font-semibold text-gray-900 dark:text-white">{fieldDef.label}</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('fieldConfigurator.configure')}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleDuplicate}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg touch-manipulation"
                title="Duplicate"
                aria-label="Duplicate field"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
              <button
                onClick={handleDelete}
                className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg touch-manipulation"
                title="Delete"
                aria-label="Delete field"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg touch-manipulation"
                aria-label="Close configurator"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mt-4 overflow-x-auto scrollbar-hide">
            <TabButton
              label="General"
              icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>}
              isActive={activeTab === 'general'}
              onClick={() => setActiveTab('general')}
            />
            {hasOptions && (
              <TabButton
                label="Options"
                icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>}
                isActive={activeTab === 'options'}
                onClick={() => setActiveTab('options')}
              />
            )}
            {hasValidation && (
              <TabButton
                label="Validation"
                icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
                isActive={activeTab === 'validation'}
                onClick={() => setActiveTab('validation')}
              />
            )}
            <TabButton
              label="Advanced"
              icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" /></svg>}
              isActive={activeTab === 'advanced'}
              onClick={() => setActiveTab('advanced')}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 pb-safe" style={{ maxHeight: 'calc(100% - 180px)' }}>
          {/* General Tab */}
          {activeTab === 'general' && (
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('fieldConfigurator.fieldLabel')}
                </label>
                <input
                  type="text"
                  value={field.label}
                  onChange={(e) => handleChange('label', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-base"
                  placeholder={t('fieldConfigurator.labelPlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('fieldConfigurator.placeholder')}
                </label>
                <input
                  type="text"
                  value={field.placeholder || ''}
                  onChange={(e) => handleChange('placeholder', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-base"
                  placeholder={t('fieldConfigurator.placeholderHelper')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Help Text
                </label>
                <input
                  type="text"
                  value={field.helpText || ''}
                  onChange={(e) => handleChange('helpText', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-base"
                  placeholder="Additional help information"
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-xl">
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">{t('fieldConfigurator.required')}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('fieldConfigurator.requiredDescription')}</p>
                </div>
                <button
                  onClick={() => handleChange('required', !field.required)}
                  role="switch"
                  aria-checked={field.required}
                  aria-label="Required field"
                  className={`relative w-14 h-8 rounded-full transition-colors touch-manipulation ${
                    field.required ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span
                    className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow transition-transform ${
                      field.required ? 'translate-x-7' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          )}

          {/* Options Tab */}
          {activeTab === 'options' && hasOptions && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {field.options?.length || 0} option{(field.options?.length || 0) !== 1 ? 's' : ''}
                </p>
              </div>

              <div className="space-y-3">
                {field.options?.map((option, index) => (
                  <OptionItem
                    key={`${index}-${option.value}`}
                    option={option}
                    index={index}
                    onUpdate={(updates) => updateOption(index, updates)}
                    onRemove={() => removeOption(index)}
                    canRemove={(field.options?.length || 0) > 1}
                  />
                ))}
              </div>

              <button
                onClick={addOption}
                className="w-full py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl text-gray-500 dark:text-gray-400 flex items-center justify-center gap-2 active:bg-gray-50 dark:active:bg-gray-700 transition-colors touch-manipulation"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                {t('fieldConfigurator.addOption')}
              </button>
            </div>
          )}

          {/* Validation Tab */}
          {activeTab === 'validation' && hasValidation && (
            <div className="space-y-5">
              {(field.type === 'text' || field.type === 'textarea') && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Min Length
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={field.properties?.minLength || ''}
                        onChange={(e) => updateField(field.id, {
                          properties: { ...field.properties, minLength: e.target.value ? parseInt(e.target.value) : undefined }
                        })}
                        className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-base"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Max Length
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={field.properties?.maxLength || ''}
                        onChange={(e) => updateField(field.id, {
                          properties: { ...field.properties, maxLength: e.target.value ? parseInt(e.target.value) : undefined }
                        })}
                        className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-base"
                        placeholder="No limit"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Pattern (Regex)
                    </label>
                    <input
                      type="text"
                      value={field.properties?.pattern || ''}
                      onChange={(e) => updateField(field.id, {
                        properties: { ...field.properties, pattern: e.target.value || undefined }
                      })}
                      className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white font-mono text-sm"
                      placeholder="e.g., ^[A-Z]+$"
                    />
                  </div>
                </>
              )}

              {field.type === 'number' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Minimum
                      </label>
                      <input
                        type="number"
                        value={field.properties?.min ?? ''}
                        onChange={(e) => updateField(field.id, {
                          properties: { ...field.properties, min: e.target.value ? parseFloat(e.target.value) : undefined }
                        })}
                        className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-base"
                        placeholder="No min"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Maximum
                      </label>
                      <input
                        type="number"
                        value={field.properties?.max ?? ''}
                        onChange={(e) => updateField(field.id, {
                          properties: { ...field.properties, max: e.target.value ? parseFloat(e.target.value) : undefined }
                        })}
                        className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-base"
                        placeholder="No max"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Step
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="any"
                      value={field.properties?.step ?? ''}
                      onChange={(e) => updateField(field.id, {
                        properties: { ...field.properties, step: e.target.value ? parseFloat(e.target.value) : undefined }
                      })}
                      className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-base"
                      placeholder="Any"
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {/* Advanced Tab */}
          {activeTab === 'advanced' && (
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Field Name (ID)
                </label>
                <input
                  type="text"
                  value={field.name}
                  onChange={(e) => handleChange('name', e.target.value.replace(/[^a-zA-Z0-9_]/g, '_'))}
                  className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white font-mono text-sm"
                  placeholder="field_name"
                />
                <p className="text-xs text-gray-400 mt-2">Used in form submissions data</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('fieldConfigurator.defaultValue')}
                </label>
                <input
                  type="text"
                  value={typeof field.defaultValue === 'string' || typeof field.defaultValue === 'number' ? field.defaultValue : ''}
                  onChange={(e) => handleChange('defaultValue', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-base"
                  placeholder={t('fieldConfigurator.defaultValuePlaceholder')}
                />
              </div>

              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl border border-yellow-200 dark:border-yellow-800">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">{t('fieldConfigurator.conditionalLogic')}</p>
                    <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                      {t('fieldConfigurator.conditionalDescription')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Done Button */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 safe-area-inset-bottom">
          <button
            onClick={onClose}
            className="w-full py-3 bg-primary-600 text-white rounded-xl font-medium shadow-lg shadow-primary-600/30 active:scale-[0.98] transition-transform touch-manipulation"
          >
            {t('quizSettings.done')}
          </button>
        </div>
      </div>
    </div>
  );
}
